import { useState, useCallback } from "react";
import { v4 as uuid } from "uuid";
import tpm from "@tvg/api/tpm";
import { TalentPickData } from "@tvg/ts-types/TalentPicks";
import { useDispatch, useSelector } from "react-redux";
import { addToast, removeToast } from "@tvg/toast-manager/src/store/actions";
import { get } from "lodash";
import {
  getTalentPicksErrorCodes,
  getTalentPicksPageMsgs
} from "../redux/selectors";
import { TpmError } from "../types";

export const useUpdateTalentPick = (published: boolean) => {
  const dispatch = useDispatch();
  const [isLoadingPickEdit, setIsLoadingPickEdit] = useState(false);
  const [isPublishedSwitchOn, setIsPublishedSwitchOn] = useState(published);
  const talentPicksErrorCodes = useSelector(getTalentPicksErrorCodes);
  const talentPicksPageMsgs = useSelector(getTalentPicksPageMsgs);

  const updateTalentPick = useCallback(
    async (updatedTalentPick: TalentPickData) => {
      const toastId = uuid();
      try {
        setIsLoadingPickEdit(true);
        await tpm.updateTalentPick(updatedTalentPick);
        setIsPublishedSwitchOn(updatedTalentPick.published);

        dispatch(
          addToast({
            id: toastId,
            variant: "informational",
            message:
              talentPicksPageMsgs.tool?.confirmModal?.syncToastText ?? "",
            qaLabel: "talent-pick-sync-toast",
            hasDismissButton: true,
            onHide: () => dispatch(removeToast(toastId)),
            onDismissPress: () => dispatch(removeToast(toastId))
          })
        );
      } catch (error) {
        setIsPublishedSwitchOn(!updatedTalentPick.published);
        console.error("Error updating talent pick:", error);

        const err = error as TpmError;
        const code = err.response?.data?.code;
        const message =
          (code && get(talentPicksErrorCodes, `${code}`, undefined)) ||
          "An unexpected error occurred while updating the talent pick.";

        dispatch(
          addToast({
            id: toastId,
            variant: "error",
            message,
            qaLabel: "update-talent-pick-error-toast",
            onHide: () => dispatch(removeToast(toastId))
          })
        );
      } finally {
        setIsLoadingPickEdit(false);
      }
    },
    [dispatch, talentPicksErrorCodes]
  );

  return {
    updateTalentPick,
    isLoadingPickEdit,
    isPublishedSwitchOn
  };
};

export default useUpdateTalentPick;
